<template>
  <div class="modal" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <!-- HEADER -->
        <div class="modal-header p-2">
          <h6 class="modal-title">{{ $t("Browse & Select Buttons") }}</h6>
          <button
            @click="closeModal()"
            type="button"
            class="close text-white"
            aria-label="Close"
          >
            <span aria-hidden="true">
              <cancel-icon
                class="close-icon"
                style="height: 30px; width: 30px; color: #fff"
              />
            </span>
          </button>
        </div>

        <!-- BODY -->
        <div class="modal-body">
          <!-- Search -->
          <div class="search-bar-comm-style mb-3">
            <input
              type="text"
              v-model="filterText"
              class="bg-transparent rounded text-light"
              placeholder="Search buttons..."
            />
            <i class="bi bi-search search-icon"></i>
          </div>

          <!-- Buttons (grouped + ungrouped together) -->
          <div class="browse-container">
            <li v-if="filteredButtons.length === 0" class="no-data-found">
              {{ $t("No data found") }}
            </li>

            <div class="buttons-flex">
              <!-- GROUPED -->
              <div
              v-for="(group, groupId) in groupedButtons"
              :key="groupId"
              class="btn-group"
              :class="{ activeButton: isGroupSelected(group) }"
            >
              <!-- Primary button -->
              <button
                class="btn"
                type="button"
                style="border-top-right-radius:0px;border-bottom-right-radius: 0;"
                :class="buttonClass(primaryBtn(group))"
                :style="buttonStyle(primaryBtn(group))"
                @click="onButtonClick(primaryBtn(group))"
                :title="primaryBtn(group).button_name"
              >
                <img 
                  v-if="primaryBtn(group).button_image" 
                  :src="getProxyConvertedURL(primaryBtn(group).button_image)" 
                  alt="btn image" 
                  class="btn-image"
                />
                <i v-else-if="primaryBtn(group).button_icon" :class="['bi','bi-'+primaryBtn(group).button_icon]"></i>
                <span v-if="primaryBtn(group).display_button_name">{{primaryBtn(group).button_name }}</span>      
              </button>
            
              <!-- Dropdown toggle with manual caret -->
              <button
                class="btn"
                type="button"
                :class="buttonClass(primaryBtn(group))"
                :style="buttonStyle(primaryBtn(group))"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                style="border-top-left-radius:0px;border-bottom-left-radius: 0;"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" 
                     class="bi bi-caret-down-fill" viewBox="0 0 16 16">
                  <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 
                           .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
                </svg>
              </button>
            
              <!-- Dropdown menu -->
              <div class="dropdown-menu p-0" style="transform: translate3d(12px, 53px, 0px);">
                <a
                  v-for="btn in getSecondaryBtns(group)"
                  :key="btn._id"
                  type="button"
                  class="dropdown-item"
                  :style="buttonStyle(btn)"
                  :class="buttonClass(btn)"
                  :title="btn.button_name"
                >
                  <img 
                    v-if="btn.button_image" 
                    :src="getProxyConvertedURL(btn.button_image)" 
                    alt="btn image" 
                    class="btn-image"
                  />
                  <i v-else-if="btn.button_icon" :class="['bi','bi-'+btn.button_icon]"></i>
                  <span v-if="btn.display_button_name">{{btn.button_name }}</span>      
                </a>
              </div>
            </div>
            

              <!-- UNGROUPED -->
                
              <template v-for="btn in ungroupedButtons">
                <div v-if="btn.status != 'deleted'" :class="[{ activeButton: buttonsIDArray.includes(btn._id) }]" :key="btn._id">
                  <button
                    class="btn"
                    type="button"
                    :style="buttonStyle(btn)"
                    :class="buttonClass(btn)"
                    :title="btn.button_name"
                    @click="toggleSelection(btn._id)"
                  >
                  <img 
                      v-if="btn.button_image" 
                      :src="getProxyConvertedURL(btn.button_image)" 
                      alt="btn image" 
                      class="btn-image"
                    />
                    <i v-else-if="btn.button_icon" :class="['bi','bi-'+btn.button_icon]"></i>
                    <span v-if="btn.display_button_name">{{btn.button_name }}</span>      
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- FOOTER -->
        <div class="modal-footer p-1 primary-btn-style">
          <button class="btn btn-outline-secondary mr-2" @click="closeModal()">
            {{ $t("Cancel") }}
          </button>
          <button
            class="btn btn-outline-primary ml-1 mr-2"
            @click="saveSelectedButtons()"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CancelIcon from "../core/InlineCancelField.vue";
import Utils from '../../components.utils.js';
export default {
  props:["selectedComponent","payloadFromParent", "alreadySelectedButtons", "configuredButtonsArray"],
  data() {
    return {
      buttonsIDArray: [],
      filterText: "",
      allButtonsArray: [...this.configuredButtonsArray]
    };
  },
  computed: {
    filteredButtons() {
      if (!this.filterText) return this.allButtonsArray;
      return this.allButtonsArray.filter(b =>
        b.button_name.toLowerCase().includes(this.filterText.toLowerCase())
      );
    },
    groupedButtons() {
      const groups = {};
      this.filteredButtons.forEach(btn => {
        if (btn.group_id) {
          if (!groups[btn.group_id]) groups[btn.group_id] = [];
          groups[btn.group_id].push(btn);
        }
      });
      // Only keep groups with 2+ buttons
      Object.keys(groups).forEach(id => {
        if (groups[id].length < 2) {
          delete groups[id];
        }
      });
      return groups;
    },
    ungroupedButtons() {
      const groupedIds = Object.keys(this.groupedButtons);
      return this.filteredButtons.filter(
        b => !b.group_id || !groupedIds.includes(b.group_id)
      );
    }
  },
  methods: {
    getProxyConvertedURL(url){
        return Utils.convertToLoginIPDomainName(url);
    },
    isGroupSelected(group) {
      if(group.length === 0) return false;
      return group.every(btn => this.buttonsIDArray.includes(btn._id));
    },
    primaryBtn(group) {
      return group.find(b => b.position === "Primary" && b.status !== "deleted") || {};
    },
    getSecondaryBtns(group) {
      return group.filter(b => b.position !== "Primary" && b.status !== "deleted");
    },
    onButtonClick(btn) {
      this.toggleSelection(btn.group_id);
    },
    buttonClass(btn) {
      if (!btn.button_color || btn.button_color.trim() === "") {
        return "btn-outline-secondary change-border";
      }
      return "";
    },
    buttonStyle(btn) {
      const baseColor = btn.button_color && btn.button_color.trim() !== "" 
        ? btn.button_color 
        : null;

      if (!baseColor) {
        return {};
      }

      if (btn.fill_background_color) {
        return {
          border: `1px solid ${baseColor}`,
          background: baseColor,
          color: this.getContrastColor(baseColor)
        };
      }

      return {
        border: `1px solid ${baseColor}`,
        background: "transparent",
        color: baseColor
      };
    },
    getContrastColor(hex) {
      let c = hex.replace("#", "");
      if (c.length === 3) {
        c = c.split("").map(ch => ch + ch).join("");
      }
      const r = parseInt(c.substr(0, 2), 16);
      const g = parseInt(c.substr(2, 2), 16);
      const b = parseInt(c.substr(4, 2), 16);

      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? "#000" : "#fff"; // dark text if bg is light
    },
    closeModal() {
      this.$emit("close");
    },

    toggleSelection(id) {
      const group = this.groupedButtons[id];
      if (group) {
        const groupIds = group.map(btn => btn._id);
        const alreadySelected = groupIds.every(btnId =>
          this.buttonsIDArray.includes(btnId)
        );

        if (alreadySelected) {
          this.buttonsIDArray = this.buttonsIDArray.filter(
            x => !groupIds.includes(x)
          );
        } else {
          this.buttonsIDArray = [...new Set([...this.buttonsIDArray, ...groupIds])];
        }
      } else {
        if (this.buttonsIDArray.includes(id)) {
          this.buttonsIDArray = this.buttonsIDArray.filter(x => x !== id);
        } else {
          this.buttonsIDArray.push(id);
        }
      }
    },
    saveSelectedButtons() {
      let payload = {};
      payload['button_ids'] = this.buttonsIDArray;
      payload['component_id'] = this.selectedComponent;
      let mergedPayload = Object.assign({}, payload, this.payloadFromParent);
      this.$emit("updateButtonsToComponents", mergedPayload);
    }
  },
  created() {
    this.alreadySelectedButtons.forEach(button => {
      if (!this.buttonsIDArray.includes(button._id)) {
        this.buttonsIDArray.push(button._id);
      }
    });
  },
  components: {
    "cancel-icon": CancelIcon
  }
};
</script>
  
  <style scoped lang="scss">
  /* --- Variables --- */
$primary-background: #1a1e21;
$secondary-background-color: #15181b;
$border-color: 1px solid #4a4a4a;
$secondary-border-color: 1px solid #2b2b2b;
$text-color: #eee;
$scrollbar-thumb-background:#333;
$scrollbar-track-background:#1a1e21;
$scrollbar-thumb-on-hover-background:#555;

/* --- General --- */
.btn {
  font-size: 14px !important;
  height: 100%;
  display: flex;
  align-items: center;
  height:38px;
}
.btn-group>.btn:not(:last-child):not(.dropdown-toggle) {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.btn-image {
  width: 25px;
  height: 25px;
  object-fit: cover;   // ensures 1:1 ratio without distortion
  border-radius: 4px;  // optional (if you want rounded edges)
  margin-inline-end: 5px;   // space before text (if both image + text ever appear)
}

.activeButton {
  border: 2px solid #007bff !important;
  padding: 4px;
  border-radius: 6px;
}

.buttons-flex {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.button-group-wrapper {
  display: flex;
}

/* --- Modal --- */
.modal {
  text-align: center;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;

  &:before {
    content: '';
    display: inline-block;
    height: 100vh;
    vertical-align: middle;
    position: fixed;
    width: 100vw;
    inset-inline-start: 0;
    top: 0px;
    background: #00000094;
  }
}

.modal-dialog {
  width: 40% !important;
  height: 60%;
  margin: 0;
  padding: 0;
  max-width: 50% !important;
  top: 3% !important;
  display: inline-block;
  text-align: start;
  vertical-align: middle;
}

.modal-content {
  height: auto;
  min-height: 100%;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  background: $primary-background !important;
  border: $secondary-border-color;

  .modal-body {
    background: $primary-background;
    height: 300px;
    padding: 10px;
    overflow: auto;
  }
}

.modal-header,
.modal-footer {
  border-top: $secondary-border-color !important;
  border-bottom: $secondary-border-color !important;
  background: $secondary-background-color;

  .modal-title {
    color: $text-color;
  }
}

.modal-header .close {
  // margin: -1rem -13px -1rem auto !important;
  margin:0px !important;
  padding: 0px !important;
}

/* --- Search bar --- */
.search-bar-comm-style {
  position: relative;
  width: 200px;
  margin: 5px 0 25px 1px;

  input {
    border: $border-color;
    height: 39px;
    padding: 0 .75rem;
    width: 100%;
    border-radius: 6px;
    background: #1e1e1e;
    color: $text-color;
  }

  .search-icon {
    position: absolute;
    inset-inline-end: 10px;
    top: 10px;
    color: #aaa;
  }
}

/* --- Dropdown --- */
.btn-group {
  position: relative;
  display: flex;
  align-items: center;
  .dropdown-menu {
    background-color: $secondary-background-color !important;
    border: $border-color;
    top: 100% !important;
    inset-inline-start: 0 !important;
    inset-inline-end: auto !important;
    transform: none !important;
    margin-top: 2px;

    .dropdown-item {
      color: $text-color;
      font-size: 14px !important;
      word-break: break-word;
      height: 40px;
      display: flex;
      align-items: center;
      gap: 7px;
    }
  }
}

/* --- Scrollbar --- */
::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-track {
  background: $scrollbar-track-background;
}
::-webkit-scrollbar-thumb {
  background: $scrollbar-thumb-background;
}
::-webkit-scrollbar-thumb:hover {
  background: $scrollbar-thumb-on-hover-background;
}

.change-border{
  border: $border-color !important;
}

.btn{
  gap:7px;
  display:flex;
}
  </style>